import type { AyzeoPromptPlatformRun, Platform, ResultValidityStatus, SectionFourResultValidity } from "./domain";

export const preferredModelOrder=["chatgpt","google-ai-overview","gemini","perplexity","claude","deepseek","grok"] as const;

export const humaniseModelId=(id:string)=>id.replace(/[-_]+/g," ").replace(/\b\w/g,letter=>letter.toUpperCase());

export function modelDisplayName(run:AyzeoPromptPlatformRun,platforms:Platform[]){
  return platforms.find(item=>item.id===run.platformId)?.name||run.suppliedModelName||humaniseModelId(run.sourceModelId||run.platformId);
}

export function sortModelRuns(runs:AyzeoPromptPlatformRun[],platforms:Platform[]){
  const preferred=new Map<string,number>(preferredModelOrder.map((id,index)=>[id,index]));
  return [...runs].sort((a,b)=>{
    const ai=preferred.get(a.platformId),bi=preferred.get(b.platformId);
    if(ai!==undefined||bi!==undefined)return (ai??Number.MAX_SAFE_INTEGER)-(bi??Number.MAX_SAFE_INTEGER);
    return modelDisplayName(a,platforms).localeCompare(modelDisplayName(b,platforms),"en-GB",{sensitivity:"base"})||a.id.localeCompare(b.id);
  });
}

export const isAssessableStatus=(status?:ResultValidityStatus)=>status==="Assessable"||status==="Valid for commercial visibility judgement";
export const isExcludedStatus=(status?:ResultValidityStatus)=>Boolean(status&&(status.startsWith("Unsuitable")||status==="Insufficient evidence"));
export const isAssessableReview=(review?:SectionFourResultValidity)=>review?.assessability==="Yes, assessable"||isAssessableStatus(review?.status);
export const isExcludedReview=(review?:SectionFourResultValidity)=>review?.assessability==="No, unsuitable"||isExcludedStatus(review?.status);
export const isUnavailableRun=(run:AyzeoPromptPlatformRun,review?:SectionFourResultValidity)=>run.responseAvailability==="Unavailable"||Boolean(run.missingReason)||review?.status==="Result unavailable";
export const isReviewableRun=(run:AyzeoPromptPlatformRun)=>run.responseAvailability!=="Unavailable"&&!run.missingReason;

export function sectionFourRunProgress(runs:AyzeoPromptPlatformRun[],reviews:SectionFourResultValidity[]){
  const reviewByRun=new Map(reviews.map(item=>[item.sourceRunId,item]));
  const reviewable=runs.filter(isReviewableRun);
  const assessed=reviewable.filter(run=>{
    const review=reviewByRun.get(run.id);
    if(!review)return false;
    if(review.reviewState)return review.reviewState==="Complete"||review.reviewState==="Needs attention";
    return review.status!=="Needs review"&&review.status!=="Result unavailable";
  });
  return{
    totalRuns:runs.length,
    reviewableRuns:reviewable.length,
    assessedRuns:assessed.length,
    assessableRuns:runs.filter(run=>isAssessableReview(reviewByRun.get(run.id))).length,
    excludedRuns:runs.filter(run=>isExcludedReview(reviewByRun.get(run.id))).length,
    unavailableRuns:runs.filter(run=>isUnavailableRun(run,reviewByRun.get(run.id))).length,
  };
}

export function executionIdentity(run:Pick<AyzeoPromptPlatformRun,"externalRunId"|"externalPromptId"|"platformId"|"executedAt"|"payloadChecksum">){
  return run.externalRunId?`external:${run.externalRunId}`:`fallback:${run.externalPromptId??""}|${run.platformId}|${run.executedAt}|${run.payloadChecksum}`;
}

export function uniqueNewExecutions(existing:AyzeoPromptPlatformRun[],incoming:AyzeoPromptPlatformRun[]){
  const identities=new Set(existing.map(executionIdentity));
  return incoming.filter(run=>{const identity=executionIdentity(run);if(identities.has(identity))return false;identities.add(identity);return true});
}
